import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { RefreshCw, Users, Clock, Settings, FileText, CheckCircle, XCircle, Loader, Car } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { ExternalImportCard } from '@/components/Import/ExternalImportCard';
import { IntegrationOpsPanel } from '@/components/Import/IntegrationOpsPanel';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { formatDate } from '@/lib/utils';
import { syncOficinaFromApi, uploadOficinaCsv } from '@/services/oficinaApi';
import type { IntegrationRunKind } from '@/services/integrationRunApi';

interface ExternalImportLogSummary {
  vencimentoMaisAntigo: string | null;
  vencimentoMaisRecente: string | null;
  empresas: Array<{
    nome: string;
    totalTitulos: number;
    clientesDistintos: number;
    locacoesDistintas: number;
  }>;
  totalClientesSemOperador?: number;
  clientesSemOperador?: Array<{
    codigo: string;
    nome: string;
    empresa: string | null;
    titulosSemOperador: number;
  }>;
}

interface ClientWithoutOperator {
  clienteId?: string;
  companyId?: string | null;
  codigo: string;
  nome: string;
  empresa: string | null;
  titulosSemOperador: number;
  saldoTotal?: number;
  valorTotal?: number;
  vencimentoMaisAntigo?: string | null;
  statusLocacaoSemKanban?: string | null;
  motivo?: string;
}

interface UnassignedTitlesFilter {
  clienteId?: string;
  clienteNome?: string;
  companyId?: string | null;
  statuses?: string;
}

interface ExternalImportLog {
  id: string;
  status: 'success' | 'error' | 'running';
  totalProcessed: number;
  totalCreated: number;
  totalUpdated: number;
  errors?: string;
  duration: number;
  message?: string;
  details?: ExternalImportLogSummary;
  executedAt: string;
  createdAt: string;
}

interface CronConfig {
  autoSyncEnabled: boolean;
  syncInterval: string;
  lastSyncAt?: string;
  lastSyncStatus?: 'success' | 'error';
  lastSyncError?: string;
  isScheduled: boolean;
  isExecuting: boolean;
  hasRunningLog?: boolean;
  importLockHeld?: boolean;
  lastExecutionStart?: string;
}

interface LocacoesCronConfig {
  locacoesAutoSyncEnabled: boolean;
  locacoesSyncInterval: string;
  locacoesLastSyncAt?: string;
  locacoesLastSyncStatus?: 'success' | 'error';
  locacoesLastSyncError?: string;
  integrationEnabled: boolean;
  isScheduled: boolean;
  isExecuting: boolean;
}

export const ImportPage: React.FC = () => {
  const [opsKindFilter, setOpsKindFilter] = useState<IntegrationRunKind | ''>('');
  const [distributing, setDistributing] = useState(false);
  const [distributionResult, setDistributionResult] = useState<any>(null);
  const [showUnassignedModal, setShowUnassignedModal] = useState(false);
  const [unassignedFilter, setUnassignedFilter] = useState<UnassignedTitlesFilter>({});
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [unassignedPage, setUnassignedPage] = useState(1);
  const [unassignedData, setUnassignedData] = useState<{
    items: Array<{
      id: string;
      numero: string;
      status: string;
      statusLocacao: string | null;
      valor: number;
      saldo: number | null;
      vencimento: string;
      placa: string | null;
      companyName: string | null;
      clienteNome: string | null;
      kanbanNome: string | null;
      kanbanEtapaNome: string | null;
    }>;
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  } | null>(null);
  const [cronConfig, setCronConfig] = useState<CronConfig | null>(null);
  const [showCronConfigModal, setShowCronConfigModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [logs, setLogs] = useState<ExternalImportLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [clientsWithoutOperator, setClientsWithoutOperator] = useState<{
    total: number;
    items: ClientWithoutOperator[];
  } | null>(null);
  const [vencidosSemOperador, setVencidosSemOperador] = useState<{
    total: number;
    items: ClientWithoutOperator[];
  } | null>(null);
  const [loadingVencidosSemOperador, setLoadingVencidosSemOperador] = useState(false);
  const [cronForm, setCronForm] = useState({
    autoSyncEnabled: false,
    syncInterval: '0 8 * * *', // Padrão: 8h da manhã todos os dias
  });
  const [savingCron, setSavingCron] = useState(false);
  const [togglingCron, setTogglingCron] = useState(false);
  const [clearingStuck, setClearingStuck] = useState(false);
  const [importingLocacoes, setImportingLocacoes] = useState(false);
  const [locacoesResult, setLocacoesResult] = useState<{
    totalReceived: number;
    totalCreated: number;
    totalUpdated: number;
    titulosStatusLocacaoUpdated?: number;
    errors: string[];
    periodo: { inicio: string; fim: string };
  } | null>(null);

  const [locacoesDataInicio, setLocacoesDataInicio] = useState('2025-06-01');
  const [locacoesCronConfig, setLocacoesCronConfig] = useState<LocacoesCronConfig | null>(null);
  const [showLocacoesCronModal, setShowLocacoesCronModal] = useState(false);
  const [locacoesCronForm, setLocacoesCronForm] = useState({
    locacoesAutoSyncEnabled: true,
    locacoesSyncInterval: '*/20 * * * *',
  });
  const [savingLocacoesCron, setSavingLocacoesCron] = useState(false);
  const [togglingLocacoesCron, setTogglingLocacoesCron] = useState(false);
  const [importingOficinaApi, setImportingOficinaApi] = useState(false);
  const [oficinaApiSyncResult, setOficinaApiSyncResult] = useState<{
    totalReceived: number;
    totalInserted: number;
    errors: string[];
    periodo: { inicio: string; fim: string };
    skipped?: boolean;
    message?: string;
  } | null>(null);
  const [showOficinaApiSyncLogModal, setShowOficinaApiSyncLogModal] = useState(false);

  const [importingOficinaCsv, setImportingOficinaCsv] = useState(false);
  const [oficinaCsvResult, setOficinaCsvResult] = useState<{
    processed: number;
    created: number;
    updated: number;
    errors: string[];
  } | null>(null);

  // Carregar configuração do cron job
  useEffect(() => {
    loadCronConfig();
    loadLocacoesCronConfig();
    loadLogs();
    void loadVencidosSemOperador();
  }, []);

  // Atualizar status de locações a cada 5 segundos se estiver executando
  useEffect(() => {
    if (!locacoesCronConfig?.isExecuting) return;

    const interval = setInterval(() => {
      loadLocacoesCronConfig();
    }, 5000);

    return () => clearInterval(interval);
  }, [locacoesCronConfig?.isExecuting]);

  // Atualizar status a cada 5 segundos se estiver executando
  useEffect(() => {
    if (!cronConfig?.isExecuting) return;

    const interval = setInterval(() => {
      loadCronConfig();
      if (showLogsModal) {
        loadLogs();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [cronConfig?.isExecuting, showLogsModal]);

  // Atualizar logs automaticamente quando o modal estiver aberto e houver execução
  useEffect(() => {
    if (!showLogsModal || !logs.some(log => log.status === 'running')) return;

    const interval = setInterval(() => {
      loadLogs();
    }, 3000);

    return () => clearInterval(interval);
  }, [showLogsModal, logs]);

  const loadCronConfig = async () => {
    try {
      const response = await api.get('/import/external/cron-config');
      const config = response.data.data;
      setCronConfig(config);
      setCronForm({
        autoSyncEnabled: config.autoSyncEnabled || false,
        syncInterval: config.syncInterval || '0 8 * * *',
      });
    } catch (error) {
      console.error('Erro ao carregar configuração do cron:', error);
    }
  };

  const loadLocacoesCronConfig = async () => {
    try {
      const response = await api.get('/import/locacoes/cron-config');
      const config = response.data.data;
      setLocacoesCronConfig(config);
      setLocacoesCronForm({
        locacoesAutoSyncEnabled: config.locacoesAutoSyncEnabled ?? true,
        locacoesSyncInterval: config.locacoesSyncInterval || '*/20 * * * *',
      });
    } catch (error) {
      console.error('Erro ao carregar configuração do cron de locações:', error);
    }
  };

  const loadLogs = async () => {
    try {
      setLoadingLogs(true);
      const [logsRes, clientsRes] = await Promise.all([
        api.get('/import/external/logs?limit=100'),
        api.get('/import/distribute/clients-without-operator?limit=100').catch(() => null),
      ]);
      setLogs(logsRes.data.data || []);
      if (clientsRes?.data?.data) {
        setClientsWithoutOperator(clientsRes.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar logs:', error);
      toast.error('Erro ao carregar logs de importação');
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadVencidosSemOperador = async () => {
    try {
      setLoadingVencidosSemOperador(true);
      const res = await api.get(
        '/import/distribute/clients-without-operator?statuses=VENCIDO,PARCIAL&limit=500'
      );
      if (res.data?.data) {
        setVencidosSemOperador(res.data.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar clientes com títulos vencidos sem operador:', error);
      toast.error(
        error.response?.data?.error || 'Erro ao carregar clientes com títulos vencidos sem operador'
      );
    } finally {
      setLoadingVencidosSemOperador(false);
    }
  };

  const handleImportLocacoes = async () => {
    try {
      setImportingLocacoes(true);
      setLocacoesResult(null);
      const dataFim = new Date().toISOString().split('T')[0]!;
      const response = await api.post('/import/locacoes', {
        dataInicio: locacoesDataInicio,
        dataFim,
      });
      const data = response.data?.data;
      if (data) {
        setLocacoesResult(data);
        toast.success(response.data?.message || 'Importação de locações concluída.');
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.message || 'Erro ao importar locações';
      toast.error(msg);
    } finally {
      setImportingLocacoes(false);
    }
  };

  const handleSyncOficinaApi = async () => {
    try {
      setImportingOficinaApi(true);
      setOficinaApiSyncResult(null);
      const result = await syncOficinaFromApi();
      setOficinaApiSyncResult(result);
      if (result.skipped) {
        toast(result.message || 'Sincronização já em andamento.');
        return;
      }
      toast.success(
        `Oficina (API): ${result.totalInserted} OS no painel (${result.totalReceived} recebidas). Tabela substituída (espelho).`
      );
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} registro(s) com aviso na importação.`, { duration: 7000 });
      }
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        'Erro ao sincronizar oficina pela API';
      toast.error(msg);
    } finally {
      setImportingOficinaApi(false);
    }
  };

  const handleOficinaCsvFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      setImportingOficinaCsv(true);
      setOficinaCsvResult(null);
      const result = await uploadOficinaCsv(file);
      setOficinaCsvResult(result);
      toast.success(
        `CSV da oficina processado: ${result.processed} linha(s), ${result.created} nova(s), ${result.updated} atualizada(s).`
      );
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} erro(s) no arquivo CSV da oficina.`, { duration: 7000 });
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err?.response?.data?.message || err?.response?.data?.error || 'Erro ao importar CSV da oficina';
      toast.error(msg);
    } finally {
      setImportingOficinaCsv(false);
    }
  };

  const handleClearStuckImport = async () => {
    try {
      setClearingStuck(true);
      const response = await api.post('/import/external/clear-stuck');
      const data = response.data?.data;
      if (data?.cleared > 0 || data?.lockReleased) {
        toast.success(response.data?.message || 'Travamento liberado.');
      } else {
        toast.success(response.data?.message || 'Nenhum travamento encontrado.');
      }
      await loadCronConfig();
      await loadLogs();
    } catch (error) {
      console.error('Erro ao limpar execução travada:', error);
      toast.error('Erro ao limpar execução travada');
    } finally {
      setClearingStuck(false);
    }
  };

  const handleSaveCronConfig = async () => {
    try {
      setSavingCron(true);
      await api.put('/import/external/cron-config', cronForm);
      toast.success('Configuração do cron job salva com sucesso');
      setShowCronConfigModal(false);
      await loadCronConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração');
    } finally {
      setSavingCron(false);
    }
  };

  const handleToggleCron = async () => {
    if (!cronConfig) return;
    
    try {
      setTogglingCron(true);
      const newStatus = !cronConfig.autoSyncEnabled;
      await api.put('/import/external/cron-config', {
        autoSyncEnabled: newStatus,
        syncInterval: cronConfig.syncInterval,
      });
      toast.success(
        newStatus 
          ? 'Importação automática ativada com sucesso' 
          : 'Importação automática desativada com sucesso'
      );
      await loadCronConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar status do agendamento');
    } finally {
      setTogglingCron(false);
    }
  };

  const handleSaveLocacoesCronConfig = async () => {
    try {
      setSavingLocacoesCron(true);
      await api.put('/import/locacoes/cron-config', locacoesCronForm);
      toast.success('Configuração da sincronização automática de locações salva');
      setShowLocacoesCronModal(false);
      await loadLocacoesCronConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração de locações');
    } finally {
      setSavingLocacoesCron(false);
    }
  };

  const handleToggleLocacoesCron = async () => {
    if (!locacoesCronConfig) return;

    try {
      setTogglingLocacoesCron(true);
      const newStatus = !locacoesCronConfig.locacoesAutoSyncEnabled;
      await api.put('/import/locacoes/cron-config', {
        locacoesAutoSyncEnabled: newStatus,
        locacoesSyncInterval: locacoesCronConfig.locacoesSyncInterval,
      });
      toast.success(
        newStatus
          ? 'Sincronização automática de locações ativada'
          : 'Sincronização automática de locações desativada'
      );
      await loadLocacoesCronConfig();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao alterar sincronização de locações');
    } finally {
      setTogglingLocacoesCron(false);
    }
  };

  const loadUnassignedTitles = async (page = 1, filter: UnassignedTitlesFilter = unassignedFilter) => {
    try {
      setUnassignedLoading(true);
      const response = await api.get('/import/distribute/unassigned', {
        params: {
          page,
          limit: 50,
          ...(filter.clienteId ? { clienteFornecedorId: filter.clienteId } : {}),
          ...(filter.companyId ? { companyId: filter.companyId } : {}),
          ...(filter.statuses ? { statuses: filter.statuses } : {}),
        },
        timeout: 60 * 1000,
      });
      setUnassignedData(response.data?.data ?? null);
      setUnassignedPage(page);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erro ao listar títulos sem operador');
    } finally {
      setUnassignedLoading(false);
    }
  };

  const openUnassignedModal = (filter: UnassignedTitlesFilter = {}) => {
    setUnassignedFilter(filter);
    setUnassignedData(null);
    setShowUnassignedModal(true);
    void loadUnassignedTitles(1, filter);
  };

  const openClientUnassignedTitles = (client: ClientWithoutOperator) => {
    if (!client.clienteId) {
      toast.error('Cliente sem identificador para listar títulos');
      return;
    }
    openUnassignedModal({
      clienteId: client.clienteId,
      clienteNome: client.nome,
      companyId: client.companyId ?? null,
      statuses: 'VENCIDO,PARCIAL',
    });
  };

  const formatMoney = (n: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

  const handleDistributeTitles = async (retryCount = 0, maxRetries = 3) => {
    try {
      if (retryCount === 0) {
        setDistributing(true);
        setDistributionResult(null);
      }

      const companiesRes = await api.get('/import/distribute/companies', {
        timeout: 60 * 1000,
      });
      const companyIds: string[] = companiesRes.data?.data?.companyIds || [];

      if (companyIds.length === 0) {
        const emptyResult = {
          distributed: 0,
          totalTitles: 0,
          operatorsUsed: [],
          clientsDistributed: 0,
          operatorDistribution: [],
          summary: {
            totalTitlesInSystem: 0,
            totalTitlesWithOperator: 0,
            totalTitlesWithoutOperator: 0,
            message: 'Nenhum título disponível para distribuição.',
          },
        };
        setDistributionResult(emptyResult);
        toast.success(emptyResult.summary.message);
        window.dispatchEvent(new CustomEvent('distributionCompleted'));
        void loadVencidosSemOperador();
        setDistributing(false);
        return;
      }

      toast(`Distribuindo em ${companyIds.length} empresa(s)...`, {
        icon: '📦',
        duration: 2500,
      });

      let totalDistributed = 0;
      let totalClients = 0;
      let lastResult: any = null;
      const operatorDistMap = new Map<
        string,
        { operatorName: string; titlesCount: number; clientsCount: number }
      >();

      for (let i = 0; i < companyIds.length; i++) {
        const companyId = companyIds[i]!;
        const isLast = i === companyIds.length - 1;

        if (companyIds.length > 1) {
          toast(`Empresa ${i + 1}/${companyIds.length}...`, {
            icon: '🏢',
            duration: 2000,
          });
        }

        const response = await api.post(
          '/import/distribute',
          { companyId, finalize: isLast },
          { timeout: 5 * 60 * 1000 }
        );
        const result = response.data.data;
        lastResult = result;
        totalDistributed += result.distributed || 0;
        totalClients += result.clientsDistributed || 0;

        for (const op of result.operatorDistribution || []) {
          const existing = operatorDistMap.get(op.operatorName);
          if (existing) {
            existing.titlesCount += op.titlesCount || 0;
            existing.clientsCount += op.clientsCount || 0;
          } else {
            operatorDistMap.set(op.operatorName, {
              operatorName: op.operatorName,
              titlesCount: op.titlesCount || 0,
              clientsCount: op.clientsCount || 0,
            });
          }
        }
      }

      const aggregated = {
        ...(lastResult || {}),
        distributed: totalDistributed,
        clientsDistributed: totalClients,
        operatorsUsed: [...operatorDistMap.keys()],
        operatorDistribution: [...operatorDistMap.values()],
        summary: {
          ...(lastResult?.summary || {}),
          message:
            totalDistributed > 0
              ? `${totalDistributed} títulos distribuídos em ${companyIds.length} empresa(s)`
              : lastResult?.summary?.message || 'Nenhum título disponível para distribuição.',
        },
      };

      setDistributionResult(aggregated);
      void loadVencidosSemOperador();

      if (totalDistributed > 0) {
        toast.success(
          `Títulos distribuídos com sucesso! ${totalDistributed} títulos atribuídos (${companyIds.length} empresa(s)).`
        );
      } else {
        toast.success(aggregated.summary.message);
      }

      window.dispatchEvent(new CustomEvent('distributionCompleted'));

      // Re-lista empresas ainda pendentes (em vez de 1 POST global)
      const pendingRes = await api.get('/import/distribute/companies', {
        timeout: 60 * 1000,
      });
      const pendingIds: string[] = pendingRes.data?.data?.companyIds || [];
      const titlesWithoutOperator =
        aggregated.summary?.totalTitlesWithoutOperator ?? pendingIds.length;

      if (pendingIds.length > 0 && retryCount < maxRetries) {
        toast(
          `Ainda há ${pendingIds.length} empresa(s) com pendências. Refazendo (tentativa ${retryCount + 1}/${maxRetries})...`,
          { icon: '🔄', duration: 3000 }
        );
        setTimeout(() => {
          handleDistributeTitles(retryCount + 1, maxRetries);
        }, 2000);
        return;
      }

      if (pendingIds.length > 0 && retryCount >= maxRetries) {
        toast.error(
          `Ainda há pendências após ${maxRetries} tentativas (${titlesWithoutOperator} sem operador estimado). Verifique os logs.`
        );
        setDistributing(false);
        return;
      }

      if (retryCount > 0) {
        toast.success('Todos os títulos foram distribuídos com sucesso!');
      }
      setDistributing(false);
    } catch (error: any) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          'Erro ao distribuir títulos'
      );
      setDistributing(false);
    }
  };


  return (
    <div className="space-y-6" data-testid="page-importacao">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Importação e Distribuição</h1>
          <p className="text-muted-foreground">Sincronize via sistema financeiro e distribua títulos entre operadores</p>
        </div>
      </div>

      <IntegrationOpsPanel
        initialKindFilter={opsKindFilter}
        onKindFilterConsumed={() => setOpsKindFilter('')}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Painel Oficina — API e CSV
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Integração direta (API)</h3>
              <p className="text-sm text-muted-foreground">
                Sincroniza com <strong>getLocacaoOrdensServicos</strong> (mesma URL base, <strong>token</strong> e CNPJs
                da importação de títulos e de <strong>get-locacoes-cliente</strong>; parâmetros de período e CNPJ na
                URL). Período: <strong>01/01/2025</strong> até{' '}
                <strong>{formatDate(new Date().toISOString().slice(0, 10))}</strong>. A tabela de OS é truncada e
                recarregada (espelho). Também roda automaticamente a cada 5 minutos no servidor.
              </p>
              <div className="flex gap-2">
                <Button type="button" onClick={handleSyncOficinaApi} disabled={importingOficinaApi} className="flex-1">
                  {importingOficinaApi ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Sincronizando…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sincronizar agora (API)
                    </>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOpsKindFilter('OFICINA_API')}>
                  <FileText className="h-4 w-4" />
                </Button>
              </div>
              {oficinaApiSyncResult && (
                <button
                  type="button"
                  onClick={() => setShowOficinaApiSyncLogModal(true)}
                  className="w-full rounded-lg border border-border p-3 bg-muted/30 space-y-1 text-sm text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <p className="font-medium flex items-center justify-between gap-2">
                    <span>Última sincronização (API)</span>
                    <span className="text-xs font-normal text-muted-foreground">Ver log</span>
                  </p>
                  <p>
                    Período: {oficinaApiSyncResult.periodo.inicio} → {oficinaApiSyncResult.periodo.fim}
                  </p>
                  <p>
                    Recebidas da API: <strong>{oficinaApiSyncResult.totalReceived}</strong> · Inseridas no painel:{' '}
                    <strong>{oficinaApiSyncResult.totalInserted}</strong>
                  </p>
                  {oficinaApiSyncResult.errors.length > 0 && (
                    <p className="text-amber-600 dark:text-amber-400 text-xs">
                      {oficinaApiSyncResult.errors.length} aviso(s):{' '}
                      {oficinaApiSyncResult.errors.slice(0, 2).join('; ')}
                      {oficinaApiSyncResult.errors.length > 2 ? '...' : ''}
                    </p>
                  )}
                </button>
              )}
            </div>

            <div className="border-t border-border pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Upload CSV (alternativa)</h3>
              <p className="text-sm text-muted-foreground">
                Envie o CSV do ERP (UTF-8, separador ponto e vírgula). Aceita o export atual (ex.: cnpj_empresa,
                nome_cliente, id_os, id_locacao, dt_inicio, dt_entrega, placa, setor, veiculo) ou o formato interno
                normalizado. Faz upsert por empresa + OS (não substitui toda a tabela como a API).
              </p>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={handleOficinaCsvFile}
                disabled={importingOficinaCsv}
              />
              {importingOficinaCsv && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader className="h-4 w-4 animate-spin" />
                  Processando arquivo…
                </div>
              )}
              {oficinaCsvResult && (
                <div className="rounded-lg border p-3 bg-muted/30 space-y-1 text-sm">
                  <p className="font-medium">Último CSV da oficina</p>
                  <p>
                    Processadas: <strong>{oficinaCsvResult.processed}</strong> · Criadas:{' '}
                    <strong>{oficinaCsvResult.created}</strong> · Atualizadas:{' '}
                    <strong>{oficinaCsvResult.updated}</strong>
                  </p>
                  {oficinaCsvResult.errors.length > 0 && (
                    <p className="text-amber-600 dark:text-amber-400 text-xs">
                      {oficinaCsvResult.errors.length} erro(s): {oficinaCsvResult.errors.slice(0, 2).join('; ')}
                      {oficinaCsvResult.errors.length > 2 ? '...' : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Importação via sistema financeiro (API externa) */}
        <ExternalImportCard 
          onImportComplete={() => {
            loadLogs(); // Recarregar logs após importação
          }}
          onAutoDistribute={handleDistributeTitles}
        />

        {/* Importação de Locações (get-locacoes-cliente - Flystart) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Car className="h-5 w-5 mr-2" />
              Importar Locações
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Sincronize as locações do Flystart (get-locacoes-cliente). Defina a data inicial; a data final será sempre o dia atual. Usa o mesmo token e CNPJ da importação de títulos.
            </p>

            {locacoesCronConfig && (
              <div className="space-y-2 rounded-lg border border-border p-3 bg-gray-50/80 dark:bg-slate-800/50">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Sincronização automática
                </p>

                {!locacoesCronConfig.integrationEnabled && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    A integração externa precisa estar habilitada (token e CNPJ) para o agendamento funcionar.
                  </p>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Agendamento:</span>
                    {locacoesCronConfig.locacoesAutoSyncEnabled ? (
                      <>
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-sm text-green-600 font-medium">Ativo</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500 font-medium">Inativo</span>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleToggleLocacoesCron}
                    disabled={togglingLocacoesCron || locacoesCronConfig.isExecuting}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${locacoesCronConfig.locacoesAutoSyncEnabled
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                      }
                      ${togglingLocacoesCron || locacoesCronConfig.isExecuting
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                      }
                    `}
                    title={
                      locacoesCronConfig.isExecuting
                        ? 'Não é possível desativar durante a execução'
                        : locacoesCronConfig.locacoesAutoSyncEnabled
                        ? 'Clique para desativar'
                        : 'Clique para ativar'
                    }
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${locacoesCronConfig.locacoesAutoSyncEnabled ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {locacoesCronConfig.isExecuting && (
                  <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                    <Loader className="h-4 w-4 animate-spin" />
                    Executando sincronização automática...
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Expressão cron:</span>
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    {locacoesCronConfig.locacoesSyncInterval}
                  </code>
                </div>

                {locacoesCronConfig.locacoesLastSyncAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Última execução:</span>
                    <span>
                      {formatDate(new Date(locacoesCronConfig.locacoesLastSyncAt), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                )}

                {locacoesCronConfig.locacoesLastSyncStatus && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Status:</span>
                    <div className="flex items-center gap-1">
                      {locacoesCronConfig.locacoesLastSyncStatus === 'success' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-600">Sucesso</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-red-600">Erro</span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {locacoesCronConfig.locacoesLastSyncError &&
                  locacoesCronConfig.locacoesLastSyncStatus === 'error' && (
                  <p className="text-xs text-red-600 dark:text-red-400 line-clamp-3">
                    {locacoesCronConfig.locacoesLastSyncError}
                  </p>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLocacoesCronModal(true)}
                  className="w-full"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Configurar agendamento
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300">Data inicial</label>
              <Input
                type="date"
                value={locacoesDataInicio}
                onChange={(e) => setLocacoesDataInicio(e.target.value)}
                className="w-full max-w-xs"
              />
              <p className="text-xs text-muted-foreground">Data final: sempre o dia atual</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleImportLocacoes}
                disabled={importingLocacoes || !locacoesDataInicio}
                className="flex-1"
              >
                {importingLocacoes ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Importando locações...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Importar Locações
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpsKindFilter('LOCACOES')}
                title="Histórico de locações"
              >
                <FileText className="h-4 w-4" />
              </Button>
            </div>
            {locacoesResult && (
              <div className="rounded-lg border p-3 bg-muted/30 space-y-1 text-sm">
                <p className="font-medium">Resultado</p>
                <p className="text-muted-foreground">
                  Período: {locacoesResult.periodo?.inicio} a {locacoesResult.periodo?.fim}
                </p>
                <p>
                  Recebidas: <strong>{locacoesResult.totalReceived}</strong> · 
                  Criadas: <strong>{locacoesResult.totalCreated}</strong> · 
                  Atualizadas: <strong>{locacoesResult.totalUpdated}</strong>
                  {locacoesResult.titulosStatusLocacaoUpdated != null && locacoesResult.titulosStatusLocacaoUpdated > 0 && (
                    <> · Títulos (statusLocacao): <strong>{locacoesResult.titulosStatusLocacaoUpdated}</strong></>
                  )}
                </p>
                {locacoesResult.errors?.length > 0 && (
                  <p className="text-amber-600 dark:text-amber-400 text-xs">
                    {locacoesResult.errors.length} aviso(s): {locacoesResult.errors.slice(0, 2).join('; ')}
                    {locacoesResult.errors.length > 2 ? '...' : ''}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuração do Cron Job */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Importação Automática
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Configure a importação automática via cron job para sincronizar títulos periodicamente.
            </p>

            {cronConfig && (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">Agendamento Automático:</span>
                    <div className="flex items-center gap-2">
                      {cronConfig.autoSyncEnabled ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-600 font-medium">Ativo</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-500 font-medium">Inativo</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleToggleCron}
                    disabled={togglingCron || cronConfig.isExecuting}
                    className={`
                      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                      focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                      ${cronConfig.autoSyncEnabled 
                        ? 'bg-green-500' 
                        : 'bg-gray-300 dark:bg-gray-600'
                      }
                      ${togglingCron || cronConfig.isExecuting 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'cursor-pointer'
                      }
                    `}
                    title={
                      cronConfig.isExecuting 
                        ? 'Não é possível desativar durante a execução'
                        : cronConfig.autoSyncEnabled
                        ? 'Clique para desativar o agendamento'
                        : 'Clique para ativar o agendamento'
                    }
                  >
                    <span
                      className={`
                        inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                        ${cronConfig.autoSyncEnabled ? 'translate-x-6' : 'translate-x-1'}
                      `}
                    />
                  </button>
                </div>

                {cronConfig.isExecuting && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Loader className="h-4 w-4 text-blue-500 animate-spin" />
                        <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                          ⚡ Executando importação...
                        </span>
                      </div>
                    </div>
                    {cronConfig.lastExecutionStart && (
                      <div className="text-xs text-blue-600 dark:text-blue-400">
                        Iniciado em: {formatDate(new Date(cronConfig.lastExecutionStart), 'dd/MM/yyyy HH:mm:ss')}
                      </div>
                    )}
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                      Aguarde enquanto os dados são sincronizados...
                    </div>
                    {cronConfig.hasRunningLog && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                        Parece que uma execução anterior ficou travada. Se já passou muito tempo,{' '}
                        <button
                          type="button"
                          onClick={handleClearStuckImport}
                          disabled={clearingStuck}
                          className="underline font-medium hover:no-underline"
                        >
                          {clearingStuck ? 'Limpando...' : 'marque como encerrada'}
                        </button>
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Agendamento:</span>
                  <span className="text-sm text-gray-600 dark:text-slate-400">
                    {cronConfig.syncInterval}
                  </span>
                </div>

                {cronConfig.lastSyncAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Última execução:</span>
                    <span className="text-sm text-gray-600 dark:text-slate-400">
                      {formatDate(new Date(cronConfig.lastSyncAt), 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                )}

                {cronConfig.lastSyncStatus && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Status última execução:</span>
                    <div className="flex items-center gap-2">
                      {cronConfig.lastSyncStatus === 'success' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-600">Sucesso</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-500" />
                          <span className="text-sm text-red-600">Erro</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCronConfigModal(true)}
                className="flex-1"
              >
                <Settings className="h-4 w-4 mr-2" />
                Configurar
              </Button>
              <Button
                variant="outline"
                onClick={() => setOpsKindFilter('TITULOS_ERP')}
                className="flex-1"
              >
                <FileText className="h-4 w-4 mr-2" />
                Histórico
              </Button>
            </div>

            {(cronConfig?.importLockHeld || cronConfig?.hasRunningLog) && !cronConfig?.isExecuting && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  Detectado possível travamento
                  {cronConfig.importLockHeld ? ' (lock MySQL)' : ''}
                  {cronConfig.hasRunningLog ? ' (log em execução)' : ''}.
                  A importação manual e o cron podem falhar até liberar.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2 border-amber-300 text-amber-900 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-100"
                  onClick={handleClearStuckImport}
                  disabled={clearingStuck}
                >
                  {clearingStuck ? 'Liberando...' : 'Liberar travamento'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Distribution Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              Distribuir Títulos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Distribua automaticamente os títulos importados entre os operadores ativos usando o algoritmo round-robin.
            </p>
            
            {distributing && (
              <div className="bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">
                      Distribuindo títulos...
                    </p>
                    <p className="text-xs text-blue-600">
                      Aguarde enquanto os títulos são atribuídos aos operadores
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button
                onClick={() => handleDistributeTitles()}
                disabled={distributing}
                className="flex-1"
              >
                {distributing ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Distribuindo...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Distribuir Títulos
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpsKindFilter('DISTRIBUICAO')}
                title="Ver logs da distribuição"
              >
                <FileText className="h-4 w-4 mr-1" />
                Logs
              </Button>
            </div>
            
            {distributionResult && !distributing && (
              <div className={`border rounded-lg p-4 space-y-3 ${
                distributionResult.distributed > 0 
                  ? 'bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800' 
                  : 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800'
              }`}>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${
                    distributionResult.distributed > 0 ? 'bg-green-500' : 'bg-blue-500'
                  }`}></div>
                  <p className={`text-sm font-medium ${
                    distributionResult.distributed > 0 ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    Distribuição Concluída
                  </p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className={`font-medium ${
                      distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                    }`}>
                      {distributionResult.distributed}
                    </p>
                    <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                      Títulos Atribuídos
                    </p>
                  </div>
                  <div>
                    <p className={`font-medium ${
                      distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                    }`}>
                      {distributionResult.clientsDistributed || 0}
                    </p>
                    <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                      Clientes Afetados
                    </p>
                  </div>
                </div>
                
                {/* Mostrar informações do summary */}
                {distributionResult.summary && (
                  <div className={`border-t pt-3 ${
                    distributionResult.distributed > 0 ? 'border-green-200' : 'border-blue-200'
                  }`}>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <p className={`font-medium ${
                          distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {distributionResult.summary.totalTitlesWithOperator}
                        </p>
                        <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                          Total com Operador
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => openUnassignedModal()}
                        disabled={!distributionResult.summary.totalTitlesWithoutOperator}
                        className={`text-left rounded-md p-1 -m-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                          distributionResult.summary.totalTitlesWithoutOperator
                            ? 'hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            : ''
                        }`}
                        title={
                          distributionResult.summary.totalTitlesWithoutOperator
                            ? 'Ver listagem dos títulos sem operador'
                            : undefined
                        }
                      >
                        <p className={`font-medium ${
                          distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {distributionResult.summary.totalTitlesWithoutOperator}
                        </p>
                        <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                          Sem Operador
                        </p>
                      </button>
                    </div>
                    <p className={`text-xs mt-2 ${
                      distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {distributionResult.summary.message}
                    </p>
                  </div>
                )}

                {/* Mostrar estatísticas de importação */}
                {distributionResult.importStats && (
                  <div className={`border-t pt-3 ${
                    distributionResult.distributed > 0 ? 'border-green-200' : 'border-blue-200'
                  }`}>
                    <p className={`text-xs font-medium mb-2 ${
                      distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                    }`}>
                      📊 Estatísticas da Importação:
                    </p>
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <p className={`font-medium ${
                          distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {distributionResult.importStats.newTitles}
                        </p>
                        <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                          Novos Títulos
                        </p>
                      </div>
                      <div>
                        <p className={`font-medium ${
                          distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {distributionResult.importStats.updatedTitles}
                        </p>
                        <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                          Títulos Atualizados
                        </p>
                      </div>
                      <div>
                        <p className={`font-medium ${
                          distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                        }`}>
                          {distributionResult.importStats.totalProcessed}
                        </p>
                        <p className={distributionResult.distributed > 0 ? 'text-green-600' : 'text-blue-600'}>
                          Total Processado
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mostrar informações de validação automática */}
                {distributionResult.validationResult && (
                  <div className={`border-t pt-3 ${
                    distributionResult.distributed > 0 ? 'border-green-200' : 'border-blue-200'
                  }`}>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className={`w-2 h-2 rounded-full ${
                        distributionResult.validationResult.validationSuccess ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                      <p className={`text-xs font-medium ${
                        distributionResult.distributed > 0 ? 'text-green-700' : 'text-blue-700'
                      }`}>
                        🔍 Validação Automática
                      </p>
                    </div>
                    
                    {distributionResult.validationResult.correctedAllocations > 0 ? (
                      <div className="text-xs">
                        <p className="text-green-600 font-medium mb-1">
                          ✅ {distributionResult.validationResult.correctedAllocations} alocações corrigidas automaticamente
                        </p>
                        {distributionResult.validationResult.validationErrors > 0 && (
                          <p className="text-yellow-600">
                            ⚠️ {distributionResult.validationResult.validationErrors} avisos durante a validação
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs">
                        <p className="text-green-600">
                          ✅ Todas as alocações estão corretas
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {distributionResult.operatorDistribution && distributionResult.operatorDistribution.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-green-700 mb-2">
                      Distribuição por Operador:
                    </p>
                    <div className="space-y-2">
                      {distributionResult.operatorDistribution.map((operator: any, index: number) => (
                        <div key={index} className="bg-card rounded border border-green-200 p-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                              <span className="text-xs font-medium text-green-700">
                                {operator.operatorName}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-xs font-bold text-green-600">
                                {operator.titlesAssigned} títulos
                              </div>
                              <div className="text-xs text-green-500">
                                {operator.clientsAssigned} clientes
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Clientes com títulos vencidos sem operador
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Clientes com ao menos um título VENCIDO ou PARCIAL sem operador alocado.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadVencidosSemOperador()}
            disabled={loadingVencidosSemOperador}
          >
            {loadingVencidosSemOperador ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Atualizar</span>
          </Button>
        </CardHeader>
        <CardContent>
          {loadingVencidosSemOperador && !vencidosSemOperador ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Carregando…
            </div>
          ) : !vencidosSemOperador || vencidosSemOperador.items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Nenhum cliente com título vencido/parcial sem operador no momento.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {vencidosSemOperador.total.toLocaleString('pt-BR')} cliente(s)
                {vencidosSemOperador.total > vencidosSemOperador.items.length
                  ? ` · exibindo ${vencidosSemOperador.items.length}`
                  : ''}
              </p>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Código</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead className="text-right">Títulos</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                      <TableHead>Venc. mais antigo</TableHead>
                      <TableHead>Motivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vencidosSemOperador.items.map((c) => (
                      <TableRow
                        key={`${c.clienteId ?? c.codigo}-${c.companyId ?? c.empresa ?? ''}`}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => openClientUnassignedTitles(c)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openClientUnassignedTitles(c);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        title="Ver títulos sem operador deste cliente"
                      >
                        <TableCell className="font-mono text-xs">{c.codigo}</TableCell>
                        <TableCell className="font-medium text-primary underline-offset-2 hover:underline">
                          {c.nome}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.empresa || '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {c.titulosSemOperador}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney(c.saldoTotal ?? 0)}
                        </TableCell>
                        <TableCell>
                          {c.vencimentoMaisAntigo
                            ? formatDate(c.vencimentoMaisAntigo)
                            : '—'}
                        </TableCell>
                        <TableCell
                          className="max-w-[18rem] text-sm text-muted-foreground"
                          title={c.motivo || undefined}
                        >
                          {c.motivo || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log da sincronização Oficina (API) */}
      <Modal
        isOpen={showUnassignedModal}
        onClose={() => {
          setShowUnassignedModal(false);
          setUnassignedFilter({});
        }}
        title={
          unassignedFilter.clienteNome
            ? `Títulos sem operador — ${unassignedFilter.clienteNome}${
                unassignedData ? ` (${unassignedData.total})` : ''
              }`
            : `Títulos sem operador${unassignedData ? ` (${unassignedData.total})` : ''}`
        }
        size="2xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {unassignedFilter.clienteNome
              ? `Títulos VENCIDO/PARCIAL sem operador do cliente ${unassignedFilter.clienteNome}.`
              : 'Títulos ABERTO, VENCIDO ou PARCIAL sem operadorId. Causas comuns: empresa sem OPERADOR ativo vinculado, cliente ausente ou kanban sem etapa.'}
          </p>

          {unassignedLoading && !unassignedData ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Carregando…
            </div>
          ) : unassignedData && unassignedData.items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Nenhum título sem operador no momento.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Número</th>
                    <th className="px-3 py-2 font-medium">Cliente</th>
                    <th className="px-3 py-2 font-medium">Empresa</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Locação</th>
                    <th className="px-3 py-2 font-medium">Kanban</th>
                    <th className="px-3 py-2 font-medium">Vencimento</th>
                    <th className="px-3 py-2 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(unassignedData?.items ?? []).map((t) => (
                    <tr key={t.id} className="border-t border-border/70">
                      <td className="px-3 py-2 font-mono text-xs">{t.numero}</td>
                      <td className="px-3 py-2 max-w-[10rem] truncate" title={t.clienteNome || undefined}>
                        {t.clienteNome || '—'}
                      </td>
                      <td className="px-3 py-2 max-w-[8rem] truncate" title={t.companyName || undefined}>
                        {t.companyName || '—'}
                      </td>
                      <td className="px-3 py-2">{t.status}</td>
                      <td className="px-3 py-2">{t.statusLocacao || '—'}</td>
                      <td className="px-3 py-2">
                        {t.kanbanNome
                          ? `${t.kanbanNome}${t.kanbanEtapaNome ? ` · ${t.kanbanEtapaNome}` : ''}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {t.vencimento ? formatDate(t.vencimento) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatMoney(t.saldo ?? t.valor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {unassignedData && unassignedData.totalPages > 1 ? (
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Página {unassignedData.page} de {unassignedData.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={unassignedLoading || unassignedPage <= 1}
                  onClick={() => void loadUnassignedTitles(unassignedPage - 1, unassignedFilter)}
                >
                  Anterior
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={unassignedLoading || unassignedPage >= unassignedData.totalPages}
                  onClick={() => void loadUnassignedTitles(unassignedPage + 1, unassignedFilter)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowUnassignedModal(false);
                setUnassignedFilter({});
              }}
            >
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showOficinaApiSyncLogModal && !!oficinaApiSyncResult}
        onClose={() => setShowOficinaApiSyncLogModal(false)}
        title="Log — sincronização Oficina (API)"
        size="xl"
      >
        {oficinaApiSyncResult && (
          <div className="space-y-4 text-sm">
            {oficinaApiSyncResult.skipped ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                {oficinaApiSyncResult.message ?? 'Execução ignorada.'}
              </p>
            ) : null}
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
                <dt className="text-xs text-muted-foreground">Período consultado</dt>
                <dd className="font-mono text-foreground">
                  {oficinaApiSyncResult.periodo.inicio} → {oficinaApiSyncResult.periodo.fim}
                </dd>
              </div>
              <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
                <dt className="text-xs text-muted-foreground">Recebidas · Inseridas</dt>
                <dd className="font-semibold tabular-nums text-foreground">
                  {oficinaApiSyncResult.totalReceived} · {oficinaApiSyncResult.totalInserted}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A tabela do painel é substituída pelo espelho da API. Várias linhas com a mesma empresa e código de OS
              viram uma só (prevalece a última). Itens com erro de validação ou permissão aparecem abaixo e não entram
              na contagem de inseridas.
            </p>
            <div>
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Detalhe ({oficinaApiSyncResult.errors.length} linha(s))
              </h4>
              {oficinaApiSyncResult.errors.length > 0 ? (
                <ol className="max-h-[min(60vh,28rem)] list-decimal space-y-2 overflow-y-auto rounded-md border border-border bg-background/80 p-3 pl-8 font-mono text-xs leading-relaxed text-foreground">
                  {oficinaApiSyncResult.errors.map((line, i) => (
                    <li key={i} className="break-words">
                      {line}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                  Nenhum aviso retornado nesta execução. Se o total recebido for maior que o inserido, a diferença
                  costuma ser duplicidade (mesma OS repetida na resposta) ou exclusão na deduplicação.
                </p>
              )}
            </div>
            <div className="flex justify-end pt-1">
              <Button type="button" variant="outline" onClick={() => setShowOficinaApiSyncLogModal(false)}>
                Fechar
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de Configuração do Cron Job */}
      <Modal
        isOpen={showCronConfigModal}
        onClose={() => setShowCronConfigModal(false)}
        title="Configurar Importação Automática"
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              💡 <strong>Dica:</strong> Você também pode ativar/desativar o agendamento diretamente no card usando o toggle.
            </p>
          </div>
          
          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={cronForm.autoSyncEnabled}
                onChange={(e) => setCronForm({ ...cronForm, autoSyncEnabled: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm font-medium">Habilitar importação automática</span>
            </label>
            <p className="text-xs text-muted-foreground ml-6">
              Quando habilitado, a importação será executada automaticamente conforme o agendamento configurado.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Expressão Cron (Agendamento)
            </label>
            <Input
              value={cronForm.syncInterval}
              onChange={(e) => setCronForm({ ...cronForm, syncInterval: e.target.value })}
              placeholder="0 8 * * *"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Formato: minuto hora dia mês dia-da-semana
              <br />
              Exemplos:
              <br />
              • <code className="bg-muted px-1 rounded">0 8 * * *</code> - Todos os dias às 8h
              <br />
              • <code className="bg-muted px-1 rounded">0 */6 * * *</code> - A cada 6 horas
              <br />
              • <code className="bg-muted px-1 rounded">0 9 * * 1-5</code> - Dias úteis às 9h
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSaveCronConfig}
              disabled={savingCron}
              className="flex-1"
            >
              {savingCron ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCronConfigModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Configuração do Cron de Locações */}
      <Modal
        isOpen={showLocacoesCronModal}
        onClose={() => setShowLocacoesCronModal(false)}
        title="Configurar Sincronização Automática de Locações"
        size="lg"
      >
        <div className="space-y-4">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              O cron usa a data inicial padrão da configuração e a data final do dia atual. Requer integração externa ativa.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={locacoesCronForm.locacoesAutoSyncEnabled}
                onChange={(e) =>
                  setLocacoesCronForm({ ...locacoesCronForm, locacoesAutoSyncEnabled: e.target.checked })
                }
                className="rounded"
              />
              <span className="text-sm font-medium">Habilitar sincronização automática</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Expressão Cron (Agendamento)
            </label>
            <Input
              value={locacoesCronForm.locacoesSyncInterval}
              onChange={(e) =>
                setLocacoesCronForm({ ...locacoesCronForm, locacoesSyncInterval: e.target.value })
              }
              placeholder="*/20 * * * *"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Exemplos:
              <br />
              • <code className="bg-muted px-1 rounded">*/20 * * * *</code> — A cada 20 minutos
              <br />
              • <code className="bg-muted px-1 rounded">0 */1 * * *</code> — A cada hora
              <br />
              • <code className="bg-muted px-1 rounded">0 8 * * *</code> — Diariamente às 8h
            </p>
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              onClick={handleSaveLocacoesCronConfig}
              disabled={savingLocacoesCron}
              className="flex-1"
            >
              {savingLocacoesCron ? (
                <>
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowLocacoesCronModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal de Logs */}
      <Modal
        isOpen={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        title="Logs de Importação Automática"
        size="xl"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Últimas 100 execuções
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadLogs}
              disabled={loadingLogs}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>

          {/* Atualizar automaticamente se houver execução em andamento */}
          {showLogsModal && logs.some(log => log.status === 'running') && (
            <div className="mb-4 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Atualizando automaticamente enquanto houver execução em andamento...</span>
              </div>
            </div>
          )}

          {loadingLogs ? (
            <div className="flex items-center justify-center h-64">
              <Loader className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Nenhum log encontrado</p>
              <p className="text-sm">Os logs aparecerão aqui após as execuções automáticas</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {clientsWithoutOperator && clientsWithoutOperator.total > 0 && (
                <div className="border rounded-lg p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-sm text-amber-900 dark:text-amber-100 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Clientes sem operador (atual)
                    </p>
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                      {clientsWithoutOperator.total.toLocaleString('pt-BR')} cliente(s)
                    </span>
                  </div>
                  <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mb-2">
                    Títulos ABERTO/VENCIDO/PARCIAL sem operadorId. Exibindo até{' '}
                    {clientsWithoutOperator.items.length}.
                  </p>
                  <div className="overflow-x-auto rounded border border-amber-200 dark:border-amber-800 max-h-48 overflow-y-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-amber-100/80 dark:bg-amber-950/40 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 font-medium">Código</th>
                          <th className="px-2 py-1 font-medium">Cliente</th>
                          <th className="px-2 py-1 font-medium">Empresa</th>
                          <th className="px-2 py-1 font-medium text-right">Títulos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clientsWithoutOperator.items.map((c) => (
                          <tr
                            key={`${c.codigo}-${c.empresa ?? ''}`}
                            className="border-t border-amber-100 dark:border-amber-900/50"
                          >
                            <td className="px-2 py-1 font-mono">{c.codigo}</td>
                            <td className="px-2 py-1">{c.nome}</td>
                            <td className="px-2 py-1 text-muted-foreground">{c.empresa || '—'}</td>
                            <td className="px-2 py-1 text-right font-medium">
                              {c.titulosSemOperador.toLocaleString('pt-BR')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {clientsWithoutOperator && clientsWithoutOperator.total === 0 && (
                <div className="border rounded-lg p-3 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-200 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 shrink-0" />
                  Nenhum cliente com títulos elegíveis sem operador no momento.
                </div>
              )}

              <div className="space-y-2">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className={`border rounded-lg p-4 ${
                    log.status === 'success'
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                      : log.status === 'error'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 border-2 border-blue-400'
                  } ${log.status === 'running' ? 'ring-2 ring-blue-400' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {log.status === 'success' ? (
                        <CheckCircle className="h-5 w-5 text-green-500" />
                      ) : log.status === 'error' ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Loader className="h-5 w-5 text-blue-500 animate-spin" />
                      )}
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">
                          {log.status === 'success'
                            ? '✅ Sucesso'
                            : log.status === 'error'
                            ? '❌ Erro'
                            : '⚡ Em execução'}
                        </span>
                        {log.status === 'running' && (
                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full animate-pulse">
                            AGORA
                          </span>
                        )}
                      </div>
                      {log.status === 'running' && (
                        <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Tempo decorrido: {Math.floor((Date.now() - new Date(log.executedAt).getTime()) / 60000)}m {Math.floor(((Date.now() - new Date(log.executedAt).getTime()) % 60000) / 1000)}s
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(new Date(log.executedAt), 'dd/MM/yyyy HH:mm:ss')}
                    </span>
                  </div>

                  {log.message && (
                    <p className="text-sm text-gray-700 dark:text-slate-300 mb-2">
                      {log.message}
                    </p>
                  )}

                  <div className="grid grid-cols-3 gap-4 text-xs mt-2">
                    <div>
                      <span className="text-muted-foreground">Processados:</span>
                      <span className="ml-1 font-medium">{log.totalProcessed}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Criados:</span>
                      <span className="ml-1 font-medium text-green-600">{log.totalCreated}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Atualizados:</span>
                      <span className="ml-1 font-medium text-blue-600">{log.totalUpdated}</span>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground mt-2">
                    Duração: {(log.duration / 1000).toFixed(2)}s
                  </div>

                  {log.details && (
                    <div className="mt-3 pt-3 border-t border-border space-y-2 text-xs">
                      {(log.details.vencimentoMaisAntigo || log.details.vencimentoMaisRecente) && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {log.details.vencimentoMaisAntigo && (
                            <div>
                              <span className="text-muted-foreground">Vencimento mais antigo:</span>
                              <span className="ml-1 font-medium">
                                {formatDate(new Date(`${log.details.vencimentoMaisAntigo}T12:00:00`), 'dd/MM/yyyy')}
                              </span>
                            </div>
                          )}
                          {log.details.vencimentoMaisRecente && (
                            <div>
                              <span className="text-muted-foreground">Vencimento mais recente:</span>
                              <span className="ml-1 font-medium">
                                {formatDate(new Date(`${log.details.vencimentoMaisRecente}T12:00:00`), 'dd/MM/yyyy')}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {log.details.empresas?.length > 0 && (
                        <div>
                          <p className="font-medium text-gray-700 dark:text-slate-300 mb-1">Por empresa</p>
                          <div className="overflow-x-auto rounded border border-border">
                            <table className="w-full text-left">
                              <thead className="bg-gray-100 dark:bg-slate-800">
                                <tr>
                                  <th className="px-2 py-1 font-medium">Empresa</th>
                                  <th className="px-2 py-1 font-medium text-right">Títulos</th>
                                  <th className="px-2 py-1 font-medium text-right">Clientes</th>
                                  <th className="px-2 py-1 font-medium text-right">Locações</th>
                                </tr>
                              </thead>
                              <tbody>
                                {log.details.empresas.map((empresa) => (
                                  <tr
                                    key={empresa.nome}
                                    className="border-t border-gray-100 dark:border-slate-700"
                                  >
                                    <td className="px-2 py-1">{empresa.nome}</td>
                                    <td className="px-2 py-1 text-right">{empresa.totalTitulos.toLocaleString('pt-BR')}</td>
                                    <td className="px-2 py-1 text-right">{empresa.clientesDistintos.toLocaleString('pt-BR')}</td>
                                    <td className="px-2 py-1 text-right">{empresa.locacoesDistintas.toLocaleString('pt-BR')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {(() => {
                        const snapshotItems = Array.isArray(log.details.clientesSemOperador)
                          ? log.details.clientesSemOperador
                          : [];
                        const fromSnapshot = snapshotItems.length > 0;
                        const fromLive =
                          !fromSnapshot &&
                          Boolean(log.message?.includes('sem operador')) &&
                          Boolean(clientsWithoutOperator?.items?.length);
                        const items = fromSnapshot
                          ? snapshotItems
                          : fromLive
                            ? clientsWithoutOperator!.items
                            : [];
                        const total =
                          log.details.totalClientesSemOperador ??
                          (fromLive ? clientsWithoutOperator!.total : items.length);

                        if (total <= 0 || items.length === 0) return null;

                        return (
                          <div className="rounded border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-2">
                            <p className="font-medium text-amber-800 dark:text-amber-200 mb-1">
                              Clientes sem operador
                              {fromSnapshot ? ' após esta sync' : ' (atual)'} (
                              {total.toLocaleString('pt-BR')})
                            </p>
                            <div className="overflow-x-auto rounded border border-amber-200 dark:border-amber-800 max-h-48 overflow-y-auto">
                              <table className="w-full text-left">
                                <thead className="bg-amber-50 dark:bg-amber-950/40 sticky top-0">
                                  <tr>
                                    <th className="px-2 py-1 font-medium">Código</th>
                                    <th className="px-2 py-1 font-medium">Cliente</th>
                                    <th className="px-2 py-1 font-medium">Empresa</th>
                                    <th className="px-2 py-1 font-medium text-right">Títulos</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {items.map((c) => (
                                    <tr
                                      key={`${log.id}-sem-op-${c.codigo}-${c.empresa ?? ''}`}
                                      className="border-t border-amber-100 dark:border-amber-900/40"
                                    >
                                      <td className="px-2 py-1 font-mono">{c.codigo}</td>
                                      <td className="px-2 py-1">{c.nome}</td>
                                      <td className="px-2 py-1">{c.empresa || '—'}</td>
                                      <td className="px-2 py-1 text-right">
                                        {c.titulosSemOperador.toLocaleString('pt-BR')}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {log.errors && (
                    <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-xs text-red-700 dark:text-red-300">
                      <strong>Erros:</strong>
                      <pre className="mt-1 whitespace-pre-wrap">{log.errors}</pre>
                    </div>
                  )}

                  {log.status === 'running' && (
                    <div className="mt-2 p-2 bg-blue-100 dark:bg-blue-900/30 rounded text-xs text-blue-700 dark:text-blue-300">
                      <div className="flex items-center gap-2">
                        <Loader className="h-3 w-3 animate-spin" />
                        <strong>Importação em andamento...</strong>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};
